import { useState } from 'react';
import type { FormEvent } from 'react';

import { countableLeaves } from '@/lib/progress';
import { useDailyLogActions, useSiteProgress, useSiteWeather } from '@/lib/queries';
import type { DailyLog, DailyLogInput, DailyLogItemInput, Uuid } from '@/lib/types';

/**
 * 작업일보 작성·수정 — 직원만 (DOC-87 §3-5 · 결정 ⑥).
 *
 * <p>front 의 `DailyReportEditor` 를 <b>가져오지 않는다</b>(eslint 가 막는다 · 결정 G). 칸은 §3-5 의
 * 여덟(날짜·날씨·기온·인원·장비·금일·명일·비고)과 공정별 실적 줄이다. 그 이상은 없다 — 포털이 가벼운
 * 이유가 그것이다(결정 ④).
 *
 * <h2>패드 가로에서는 두 열</h2>
 * 레노버 패드를 가로로 들고 적는 화면이 기본이다. 짧은 칸(날짜·날씨·기온·인원)은 두 열로, 긴 칸(금일·명일)은
 * 나란히, 실적 줄은 한 줄에 공정·수량·단위·진행율이 든다. 세로·폰에서는 한 열로 흐른다.
 *
 * <h2>실적 줄의 공정은 리프만</h2>
 * 부모 공정·「소계」 줄에 실적을 적으면 접을 때 두 번 세어진다(`lib/progress.ts`). 고를 수 있는 것은
 * `countableLeaves` 가 걸러 준 말단 공정뿐이다 — 서버의 `WbsService.rollup` 과 같은 규칙.
 *
 * <h2>버튼은 제목 줄에</h2>
 * 저장·취소가 편집 카드의 머리에 선다. 긴 폼을 내려간 뒤 다시 올라오지 않는다.
 */
export function DailyLogEditor({
  siteId,
  initial,
  onSaved,
  onCancel,
}: {
  siteId: Uuid;
  /** 고치는 일보. 새로 쓰면 null. */
  initial: DailyLog | null;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const progress = useSiteProgress(siteId);
  const weather = useSiteWeather(siteId);
  const { save } = useDailyLogActions(siteId);

  const [logDate, setLogDate] = useState(initial?.logDate ?? todayIso());
  const [weatherText, setWeatherText] = useState(initial?.weather ?? '');
  const [temperature, setTemperature] = useState(initial?.temperature ?? '');
  const [manpowerCount, setManpowerCount] = useState(initial?.manpowerCount === null || initial === null ? '' : String(initial.manpowerCount));
  const [equipmentSummary, setEquipmentSummary] = useState(initial?.equipmentSummary ?? '');
  const [todayWork, setTodayWork] = useState(initial?.todayWork ?? '');
  const [tomorrowPlan, setTomorrowPlan] = useState(initial?.tomorrowPlan ?? '');
  const [progressNote, setProgressNote] = useState(initial?.progressNote ?? '');
  const [items, setItems] = useState<ItemDraft[]>(
    (initial?.items ?? []).map((it) => ({
      key: it.id,
      wbsItemId: it.wbsItemId,
      quantity: it.quantity === null ? '' : String(it.quantity),
      unit: it.unit ?? '',
      progressRate: it.progressRate === null ? '' : String(it.progressRate),
      note: it.note ?? '',
    })),
  );
  const [failed, setFailed] = useState(false);

  const leaves = countableLeaves(progress.data ?? []);

  async function onFetchWeather() {
    try {
      const got = await weather.mutateAsync(logDate);
      // 못 받은 것과 적으면 안 되는 것은 다르다 — 칸을 잠그지 않고 값이 온 것만 채운다.
      if (got.weather !== null) setWeatherText(got.weather);
      if (got.temperature !== null) setTemperature(got.temperature);
    }
    catch {
      // 날씨는 편의다. 못 받으면 손으로 적는다 — 사유는 서버 로그에 있다.
    }
  }

  function addItem() {
    const first = leaves[0];
    setItems((prev) => [
      ...prev,
      { key: `new-${Date.now()}-${prev.length}`, wbsItemId: first?.id ?? '', quantity: '', unit: '', progressRate: '', note: '' },
    ]);
  }

  function patchItem(key: string, patch: Partial<ItemDraft>) {
    setItems((prev) => prev.map((it) => (it.key === key ? { ...it, ...patch } : it)));
  }

  function removeItem(key: string) {
    setItems((prev) => prev.filter((it) => it.key !== key));
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setFailed(false);
    const body: DailyLogInput = {
      logDate,
      weather: blankToNull(weatherText),
      temperature: blankToNull(temperature),
      manpowerCount: toNumber(manpowerCount),
      equipmentSummary: blankToNull(equipmentSummary),
      todayWork: blankToNull(todayWork),
      tomorrowPlan: blankToNull(tomorrowPlan),
      progressNote: blankToNull(progressNote),
      items: items
        .filter((it) => it.wbsItemId !== '')
        .map<DailyLogItemInput>((it) => ({
          wbsItemId: it.wbsItemId,
          quantity: toNumber(it.quantity),
          unit: blankToNull(it.unit),
          progressRate: toNumber(it.progressRate),
          note: blankToNull(it.note),
        })),
    };
    try {
      await save.mutateAsync({ id: initial?.id ?? null, ...body });
      onSaved();
    }
    catch {
      // 사유는 관리자에게 — 화면은 「저장하지 못했습니다」까지다.
      setFailed(true);
    }
  }

  return (
    <form onSubmit={onSubmit} className="card mb-3">
      <div className="mb-4 flex items-center justify-between gap-2 border-b pb-3" style={{ borderColor: 'var(--border)' }}>
        <h2 className="min-w-0 truncate text-ui-md font-semibold">{initial === null ? '작업일보 작성' : '작업일보 수정'}</h2>
        <div className="flex shrink-0 items-center gap-2">
          <button type="button" className="btn-secondary px-3 py-1.5 text-ui-xs" onClick={onCancel} disabled={save.isPending}>
            취소
          </button>
          <button type="submit" className="btn-primary px-3 py-1.5 text-ui-xs" disabled={save.isPending}>
            {save.isPending ? '저장 중입니다…' : '저장'}
          </button>
        </div>
      </div>

      {failed && (
        <p className="mb-3 text-ui-xs" style={{ color: 'var(--danger)' }}>저장하지 못했습니다.</p>
      )}

      {/* 짧은 칸 — 패드 가로에서 두 열, 넓으면 네 열. */}
      <div className="mb-3 grid gap-3 md:grid-cols-2 lg:grid-cols-4">
        <label className="block">
          <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>일자</span>
          <input
            type="date"
            className="input-field"
            value={logDate}
            onChange={(e) => setLogDate(e.target.value)}
            // 하루 한 장이라 날짜는 만들 때만 고른다. 고칠 때 바꾸면 다른 날의 한 장과 부딪힌다.
            disabled={initial !== null}
            required
          />
        </label>
        <label className="block">
          <span className="mb-1 flex items-center justify-between text-ui-xs" style={{ color: 'var(--text-secondary)' }}>
            날씨
            <button
              type="button"
              className="text-ui-2xs font-medium"
              style={{ color: 'var(--accent)' }}
              onClick={onFetchWeather}
              disabled={weather.isPending}
            >
              {weather.isPending ? '가져오는 중…' : '가져오기'}
            </button>
          </span>
          <input type="text" className="input-field" value={weatherText} onChange={(e) => setWeatherText(e.target.value)} placeholder="맑음" />
        </label>
        <label className="block">
          <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>기온</span>
          <input type="text" className="input-field" value={temperature} onChange={(e) => setTemperature(e.target.value)} placeholder="24℃" />
        </label>
        <label className="block">
          <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>총 인원</span>
          <input
            type="text"
            inputMode="numeric"
            className="input-field"
            value={manpowerCount}
            onChange={(e) => setManpowerCount(e.target.value)}
            placeholder="0"
          />
        </label>
      </div>

      <label className="mb-3 block">
        <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>투입 장비</span>
        <input type="text" className="input-field" value={equipmentSummary} onChange={(e) => setEquipmentSummary(e.target.value)} placeholder="타워크레인 1, 굴착기 1" />
      </label>

      {/* 긴 칸 — 패드 가로에서 금일·명일이 나란히. */}
      <div className="mb-3 grid gap-3 md:grid-cols-2">
        <label className="block">
          <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>금일 작업</span>
          <textarea className="input-field" rows={4} value={todayWork} onChange={(e) => setTodayWork(e.target.value)} />
        </label>
        <label className="block">
          <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>명일 계획</span>
          <textarea className="input-field" rows={4} value={tomorrowPlan} onChange={(e) => setTomorrowPlan(e.target.value)} />
        </label>
      </div>

      <label className="mb-4 block">
        <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>비고</span>
        <textarea className="input-field" rows={2} value={progressNote} onChange={(e) => setProgressNote(e.target.value)} />
      </label>

      {/* ── 공정별 실적 ─────────────────────────────────────────────────── */}
      <div className="mb-2 flex items-center justify-between">
        <span className="text-ui-xs font-medium">공정별 실적</span>
        <button
          type="button"
          className="btn-secondary px-3 py-1.5 text-ui-xs"
          onClick={addItem}
          disabled={leaves.length === 0}
        >
          줄 추가
        </button>
      </div>

      {items.length > 0 && (
        <ul className="flex flex-col gap-2">
          {items.map((it) => (
            <li key={it.key} className="rounded-md border p-2" style={{ borderColor: 'var(--border)' }}>
              {/* 한 줄에 공정·수량·단위·진행율. 패드 가로에서 열둘 격자, 좁으면 두 줄로 접힌다. */}
              <div className="grid grid-cols-2 gap-2 md:grid-cols-12">
                <label className="col-span-2 block md:col-span-5">
                  <span className="mb-1 block text-ui-2xs" style={{ color: 'var(--text-muted)' }}>공정</span>
                  <select className="input-field" value={it.wbsItemId} onChange={(e) => patchItem(it.key, { wbsItemId: e.target.value })} required>
                    <option value="">선택</option>
                    {leaves.map((leaf) => (
                      <option key={leaf.id} value={leaf.id}>{leaf.code} {leaf.name}</option>
                    ))}
                  </select>
                </label>
                <label className="block md:col-span-2">
                  <span className="mb-1 block text-ui-2xs" style={{ color: 'var(--text-muted)' }}>수량</span>
                  <input type="text" inputMode="decimal" className="input-field" value={it.quantity} onChange={(e) => patchItem(it.key, { quantity: e.target.value })} />
                </label>
                <label className="block md:col-span-2">
                  <span className="mb-1 block text-ui-2xs" style={{ color: 'var(--text-muted)' }}>단위</span>
                  <input type="text" className="input-field" value={it.unit} onChange={(e) => patchItem(it.key, { unit: e.target.value })} placeholder="m3" />
                </label>
                <label className="block md:col-span-2">
                  <span className="mb-1 block text-ui-2xs" style={{ color: 'var(--text-muted)' }}>진행율 %</span>
                  <input type="text" inputMode="decimal" className="input-field" value={it.progressRate} onChange={(e) => patchItem(it.key, { progressRate: e.target.value })} />
                </label>
                <div className="flex items-end md:col-span-1">
                  <button
                    type="button"
                    className="btn-secondary w-full px-2"
                    style={{ color: 'var(--danger)' }}
                    onClick={() => removeItem(it.key)}
                    aria-label="줄 삭제"
                  >
                    ×
                  </button>
                </div>
              </div>
              <input
                type="text"
                className="input-field mt-2"
                value={it.note}
                onChange={(e) => patchItem(it.key, { note: e.target.value })}
                placeholder="비고"
              />
            </li>
          ))}
        </ul>
      )}
    </form>
  );
}

/** 입력 중인 실적 한 줄. 숫자 칸을 문자열로 두는 것은 「1.」 처럼 치는 중인 값이 지워지지 않게 하려는 것이다. */
type ItemDraft = {
  key: string;
  wbsItemId: string;
  quantity: string;
  unit: string;
  progressRate: string;
  note: string;
};

function blankToNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

function toNumber(value: string): number | null {
  const cleaned = value.replace(/[^0-9.-]/g, '');
  if (cleaned === '') return null;
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : null;
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}
